import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ConsentService } from './consent.service';

/**
 * First-layer consent banner with an expandable settings layer.
 *
 * Rendered by ConsumerLayoutComponent only while `ConsentService.bannerOpen()`
 * is true, so a new instance exists per open and focus moves to it each time.
 * Accept and reject are the same control with the same weight, side by side, and
 * both settle the choice in one click; the settings layer is an addition, never
 * a detour that rejecting has to take.
 */
@Component({
  selector: 'app-consent-banner',
  imports: [MatButtonModule, MatCheckboxModule],
  templateUrl: './consent-banner.component.html',
  styleUrl: './consent-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsentBannerComponent {
  private readonly consent = inject(ConsentService);
  private readonly panel = viewChild.required<ElementRef<HTMLElement>>('panel');

  protected readonly showSettings = signal(false);
  /** Pending selection in the settings layer; only applied on save. */
  protected readonly analyticsChoice = signal(false);
  protected readonly marketingChoice = signal(false);

  constructor() {
    afterNextRender(() => this.panel().nativeElement.focus());
  }

  protected openSettings(): void {
    // Reopened from the footer, the toggles have to show what is stored now rather
    // than the off default, or withdrawing would look like it had no effect.
    this.analyticsChoice.set(this.consent.analyticsGranted());
    this.marketingChoice.set(this.consent.marketingGranted());
    this.showSettings.set(true);
  }

  protected acceptAll(): void {
    this.consent.acceptAll();
  }

  protected rejectAll(): void {
    this.consent.rejectAll();
  }

  protected saveSelection(): void {
    this.consent.save({ analytics: this.analyticsChoice(), marketing: this.marketingChoice() });
  }
}
